import React, { useEffect, useState } from 'react'
import {
  Checkbox,
  createStyles,
  Fade,
  FormControlLabel,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import clsx from 'clsx'
import Loader from '../Loader/Loader'
import Button from '../Button/Button'
import { connect } from 'react-redux'
import { useRouter } from 'next/router'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { Cart, GAEvent } from '@/typings/base'
import { createCheckoutPayment } from '@/redux/actions/checkout'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { numberToPrice } from '@/utils/utils'
import Link from '../Link/Link'
import CMSText from '../Global/CMSText'
import useCommonStyles from '../../components/PaymentForm/CardForm.style'
import { isSubscription, minimumCartItemsNotReached } from '@/utils/constants'
import { postDadPayment, postSubPayment } from '@/redux/actions/payment'
import * as gtag from '../../utils/gtag'
import {
  clearItemVariantFromGAParamsIfNotChalhoub,
  getPaymentInfoGtmData,
  isChalhoub,
  safeDataLayerPush,
} from '@/utils/gtag'
import useRetractationEffect from './hook/useRetractationEffect'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    payStatus: {
      minHeight: '2em',
      margin: theme.spacing(0, 0, 2),
    },
  })
)

type Tprops = {
  cart: Cart
  t: any
  text: any
  settings: any
  postDadPayment: (data: object, token: string) => any
  postSubPayment: (data: object, token: string) => any
  postCheckout: (token: string, data: object) => any
  createCheckoutPayment: (token: string) => any
  startLeasingDate?: string
  coupon?: string
  swikId?: string
  disabled?: boolean
}

const PaymentFormStripe = ({
  cart,
  postDadPayment,
  postSubPayment,
  t,
  text,
  settings,
  createCheckoutPayment,
  startLeasingDate,
  coupon,
  swikId,
  disabled,
}: Tprops) => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()
  const router = useRouter()
  const [cardNumberComplete, setCardNumberComplete] = useState(false)
  const [expiryDateComplete, setExpiryDateComplete] = useState(false)
  const [cvcComplete, setCvcComplete] = useState(false)
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [paymentFailed, setPaymentFailed] = useState(true)
  const [cgu, setCgu] = useState(false)
  const [cguError, setCguError] = useState(false)
  const { trackEvent, pushInstruction } = useMatomo()
  const [retractReady, setRetractReady] = useState(false)
  const [retractAgree, setRetractAgree] = useState(false)
  const isRetractation = text?.enable_retractation_agreement
  const retractingDays = text?.retractation_duration
  const [registeredStoreSlug, setRegisteredStoreSlug] = useState('')

  useEffect(() => {
    typeof localStorage !== 'undefined' &&
      setRegisteredStoreSlug(
        JSON.parse(localStorage.getItem('selectedStore'))?.slug
      )
  }, [])

  const changeCgu = () => {
    setCgu(!cgu)
  }

  const changeRetract = () => {
    setRetractAgree(!retractAgree)
  }

  const [error, setError] = useState(null)
  const stripe = useStripe()
  const elements = useElements()

  useEffect(() => {
    if (router?.query?.insufficient_funds) {
      setError(t('texts:funnel:payment:errors:insufficient_funds'))
    } else if (router?.query?.error_3D) {
      setError(t('texts:funnel:payment:error'))
    }
  }, [router?.query])

  useRetractationEffect({
    startLeasingDate,
    isRetractation,
    retractingDays,
    setRetractReady,
    setRetractAgree,
  })

  // Handle form submission.
  const handleSubmit = async (event) => {
    if (cgu && retractAgree) {
      setCguError(false)
      setPaymentProcessing(true)
      trackEvent({
        category: 'Payment',
        action: `User click on pay button`,
      })
      if (cardNumberComplete && expiryDateComplete && cvcComplete) {
        event.preventDefault()
        const card = elements.getElement(CardNumberElement)
        const result = await stripe.createToken(card)
        const linkLocal = router.locale ? `/${router.locale}` : ''
        if (result.error) {
          // Inform the user if there was an error.
          setError(result.error.message)
          setPaymentProcessing(false)
          setPaymentFailed(true)
        } else {
          await createCheckoutPayment(cart.item.tokenValue)
          setError(null)
          const data = {
            email: localStorage.getItem('email'),
            last4: result.token.card.last4,
            token: result.token.id,
            frontSuccessPaymentUrl: `${window.location.origin}${linkLocal}/confirmation`,
            frontFailPaymentUrl: `${window.location.origin}${linkLocal}/payment?error_3D=true`,
            swik_id: swikId,
          }
          // add parcelShop id if exist
          if (
            cart?.item?.shipments[0].method?.type !== 'HOME_DELIVERY' &&
            typeof localStorage.getItem('rmsParcelShopId') !== 'undefined' &&
            localStorage.getItem('rmsParcelShopId') !== null
          ) {
            data['parcelShopId'] = localStorage.getItem('rmsParcelShopId')
          }
          let payment = null
          if (!isSubscription) {
            payment = await postDadPayment(data, cart.item.tokenValue)
          } else {
            payment = await postSubPayment(data, cart.item.tokenValue)
          }

          if (
            payment?.paymentResult?.stripe_payment_status === 'requires_action'
          ) {
            // Because of 3d secure we open a new window, causing an unstable redux state.
            // We set a local storage item in order to keep track of payment in confirmation page
            localStorage.setItem('Payment type', '3dSecure')
            window
              .open(
                payment.paymentResult.next_action.redirect_to_url.url,
                '_self'
              )
              .focus()
          } else if (payment.paymentResult === 'Order completed') {
            setPaymentProcessing(false)
            setPaymentFailed(false)
            // localStorage.removeItem('CartToken')
            // https://matomo.org/docs/ecommerce-analytics/#how-to-set-up-ecommerce-tracking-in-matomo
            await pushInstruction(
              'trackEcommerceOrder',
              cart?.item?.tokenValue, // orderId (Required) – String – A unique reference number to avoid duplication.
              numberToPrice(cart?.item?.totals?.total), // revenue (Required) – Integer/Float – The order total including tax & shipping with any discounts subtracted.
              numberToPrice(
                cart?.item?.totals?.total - cart?.item?.totals?.shipping
              ), // subTotal (Optional) – Integer/Float – The order total excluding shipping.
              numberToPrice(cart?.item?.totals?.taxes), // tax (Optional) – Integer/Float -The amount of tax charged.
              numberToPrice(cart?.item?.totals?.shipping), // shipping (Optional) – Integer/Float – The amount charged for shipping.
              numberToPrice(cart?.item?.totals?.promotion) // discount (Optional) – Integer/Float/Boolean – Discount offered? Default to false. Otherwise, you should include a numeric value.
            )

            const gaParams = {
              transaction_id: cart?.item?.tokenValue,
              currency: cart.item.currency,
              value: numberToPrice(cart?.item?.totals?.total),
              tax: numberToPrice(cart?.item?.totals?.taxes),
              shipping: numberToPrice(cart?.item?.totals?.shipping),
              items: cart.item.items.map(({ product, quantity, total }) => {
                const firstVariant = Object.values(product?.variants)[0]
                return {
                  ['item_name']: product.name,
                  ['item_id']: isChalhoub ? firstVariant.sku : product.code,
                  ['item_variant']: isChalhoub && firstVariant.code,
                  price: numberToPrice(total),
                  quantity,
                }
              }),
            }

            const cleanGAParams =
              clearItemVariantFromGAParamsIfNotChalhoub(gaParams)
            gtag.tracker(GAEvent.PURCHASE, cleanGAParams)

            safeDataLayerPush(getPaymentInfoGtmData(cart, coupon, 'stripe'))

            // Meta Pixel: Purchase event with amount and currency
            if (
              typeof window !== 'undefined' &&
              typeof window.fbq === 'function'
            ) {
              window.fbq('track', 'Purchase', {
                value: numberToPrice(cart?.item?.totals?.total),
                currency: cart?.item?.currency,
              })
            }

            await router.push('/confirmation')
          } else {
            if (payment?.err?.response?.data === minimumCartItemsNotReached) {
              setError(t('texts:funnel:payment:errors:minimum_cart_items'))
            } else {
              const error = payment?.err?.response?.data?.error
              if (error === 'some products are not available in stocks') {
                let productName = ''
                cart?.item?.items.map((products) => {
                  productName = products.product.variants.find(
                    (variants) =>
                      variants.code ===
                      payment?.err?.response?.data
                        ?.unavailableProductVariants[0]
                  ).name
                })
                setError(
                  `${t('texts:funnel:payment:errors:stock')} : ${productName}`
                )
              } else {
                if (error?.code === 'card_declined') {
                  switch (error?.decline_code) {
                    case 'insufficient_funds':
                      setError(
                        t('texts:funnel:payment:errors:insufficient_funds')
                      )
                      break
                    case 'lost_card':
                      setError(t('texts:funnel:payment:errors:lost_card'))
                      break
                    case 'stolen_card':
                      setError(t('texts:funnel:payment:errors:stolen_card'))
                      break
                    case 'Invalid email address':
                      setError(t('texts:funnel:payment:errors:email'))
                      break
                    default:
                      setError(t('texts:funnel:payment:errors:default'))
                      break
                  }
                } else {
                  switch (error?.code) {
                    case 'expired_card':
                      setError(t('texts:funnel:payment:errors:expired_card'))
                      break
                    case 'incorrect_cvc':
                      setError(t('texts:funnel:payment:errors:incorrect_cvc'))
                      break
                    case 'processing_error':
                      setError(
                        t('texts:funnel:payment:errors:processing_error')
                      )
                      break
                    default:
                      setError(t('texts:funnel:payment:errors:default'))
                      break
                  }
                }
              }
            }
            setPaymentProcessing(false)
            setPaymentFailed(true)
          }
          // Send the token to your server.
        }
      } else {
        setPaymentFailed(true)
      }
    } else {
      setCguError(true)
    }
  }

  return (
    <div className={classes.root} id={'root'}>
      <form name="payment-form" className={commonClasses.form}>
        <div>
          <div>
            <div className={clsx('label', commonClasses.label)}>
              <CMSText
                asText
                data={settings?.card_number}
                defaultText={t('texts:general:card_number')}
              />
            </div>
            <CardNumberElement
              className={clsx('input', commonClasses.input)}
              onChange={(e) => setCardNumberComplete(e.complete)}
            />
          </div>
          <Grid container justify={'space-between'}>
            <Grid xs={5} item>
              <div className={clsx('label', commonClasses.label)}>
                <CMSText
                  asText
                  data={settings?.expiry_date}
                  defaultText={t('texts:general:expiry_date')}
                />
              </div>
              <CardExpiryElement
                className={clsx('input', commonClasses.input)}
                onChange={(e) => setExpiryDateComplete(e.complete)}
              />
            </Grid>
            <Grid xs={5} item className="cb-infos">
              <div className={clsx('label', commonClasses.label)}>
                <CMSText
                  asText
                  data={settings?.cvc_code}
                  defaultText={t('texts:general:cvc_code')}
                />
              </div>
              <CardCvcElement
                className={clsx('input', commonClasses.input)}
                onChange={(e) => setCvcComplete(e.complete)}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Checkbox
                checked={cgu}
                onChange={changeCgu}
                name="checkedA"
                data-testid={'cgu_check'}
              />
            }
            label={
              <Typography
                variant={'body2'}
                component={'span'}
                className={commonClasses.cgu}
              >
                <CMSText
                  data={text?.cgu}
                  defaultText={t('texts:funnel:payment:cgu')}
                />
              </Typography>
            }
          />
          {registeredStoreSlug && (
            <Typography
              variant={'body2'}
              component={'span'}
              className={commonClasses.customCgl}
            >
              <Link href={`/pages/cgl-${registeredStoreSlug}`}>
                <a>
                  <CMSText
                    data={text?.custom_cgu_link}
                    asText
                    defaultText={t('texts:funnel:payment:custom_cgu_link')}
                  />
                </a>
              </Link>
            </Typography>
          )}
        </div>
        {isRetractation && retractReady && (
          <FormControlLabel
            control={
              <Checkbox
                checked={retractAgree}
                onChange={changeRetract}
                name="retractation"
                data-testid={'retract_check'}
              />
            }
            label={
              <Typography
                variant={'body2'}
                component={'span'}
                className={commonClasses.cgu}
              >
                <CMSText
                  data={text?.retractation_agreement}
                  defaultText={t('texts:funnel:payment:retractation_agreement')}
                />
              </Typography>
            }
          />
        )}
        {paymentFailed && (
          <Typography
            color={'error'}
            variant={'caption'}
            align={'center'}
            component="div"
          >
            {error}
          </Typography>
        )}
        {cguError && (
          <Typography
            color={'secondary'}
            variant={'caption'}
            align={'center'}
            component="div"
          >
            <CMSText
              asText
              data={text?.cgu_error}
              defaultText={t('texts:funnel:payment:cgu_error')}
            />
          </Typography>
        )}
        <div className={commonClasses.centeredButton}>
          <Button
            disabled={
              paymentProcessing ||
              !cardNumberComplete ||
              !expiryDateComplete ||
              !cvcComplete ||
              !cgu ||
              !retractAgree ||
              disabled
            }
            className={clsx(commonClasses.submit, 'payment-form__submit', {
              [commonClasses.inProgress]: paymentProcessing,
              'payment-form__submit-progress': paymentProcessing,
            })}
            variant="contained"
            data-testid="paymentSubmit"
            color="primary"
            onClick={handleSubmit}
            type="submit"
          >
            <span
              className={
                paymentProcessing ? commonClasses.hide : commonClasses.show
              }
            >
              <CMSText
                asText
                data={text?.pay}
                defaultText={t('texts:funnel:pay')}
              />
            </span>
            {paymentProcessing && <Loader />}
          </Button>
          <div className={classes.payStatus}>
            {paymentProcessing && (
              <Fade in>
                <Typography component="span" variant="body2">
                  {t('texts:funnel:payment:progress')}
                </Typography>
              </Fade>
            )}
            {!paymentFailed && (
              <Fade in>
                <Typography
                  color={'secondary'}
                  variant={'body1'}
                  align={'center'}
                >
                  {error}
                </Typography>
              </Fade>
            )}
            {cguError && (
              <Fade in>
                <Typography
                  color={'secondary'}
                  variant={'body1'}
                  align={'center'}
                >
                  <CMSText
                    asText
                    data={text?.cgu_error}
                    defaultText={t('funnel:cgu_error')}
                  />
                </Typography>
              </Fade>
            )}
          </div>
        </div>
        {!paymentFailed && (
          <Fade in>
            <div className={commonClasses.overlay}>
              <CheckCircleIcon className={commonClasses.overlayIcon} />
              <Typography component="h2" variant="h3">
                {t('texts:funnel:payment:success')}
              </Typography>
            </div>
          </Fade>
        )}
      </form>
    </div>
  )
}

const mapStateToProps = (state) => ({
  cart: state.cart,
  //user: state.user,
})

const mapDispatchToProps = {
  postDadPayment: postDadPayment,
  postSubPayment: postSubPayment,
  createCheckoutPayment: createCheckoutPayment,
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentFormStripe)
